import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, Capabilities } from '@/api/client'
import { formatValue } from '@/charts/palette'
import { useMe } from '@/features/auth/useAuth'
import { Select } from '@/components/Select'
import { BonusConfigDialog, type BonusConfig } from './BonusConfigDialog'
import { fmtEur, intlLocale, useT } from '@/i18n'
import { bonusMessages } from '@/i18n/messages/bonus'

interface BonusRow {
  crew: string
  firma: string | null
  firmaType: string | null
  days: number
  metres: number
  hours: number
  earnings: number
  perHour: number
  normPct: number
  meal: number
  leaderBonus: number
  total: number
}
interface BonusResponse {
  period: { year: number; month: number } | null
  config: BonusConfig
  rows: BonusRow[]
}

type SortKey = 'crew' | 'days' | 'metres' | 'hours' | 'earnings' | 'perHour' | 'normPct' | 'total'
/** "Sep 2026" in the reader's language. Built from parts, never through a timezone. */
const monthLabel = (year: number, month: number) =>
  `${new Intl.DateTimeFormat(intlLocale(), { month: 'short', timeZone: 'UTC' }).format(Date.UTC(2000, month - 1, 1))} ${year}`

const sortVal = (r: BonusRow, k: SortKey): number | string =>
  k === 'crew' ? r.crew.toLowerCase() : (r[k] as number)

export function BonusPage() {
  const t = useT(bonusMessages)
  const { data: me } = useMe()
  const canEdit = me?.capabilities.includes(Capabilities.EditAufmass) ?? false

  const [ym, setYm] = useState<{ year: number; month: number } | null>(null)
  const [firma, setFirma] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'total', dir: 'desc' })
  const [showConfig, setShowConfig] = useState(false)

  const { data, isPending } = useQuery<BonusResponse>({
    queryKey: ['bonus', ym, firma],
    queryFn: () => {
      const p = new URLSearchParams()
      if (ym) { p.set('year', String(ym.year)); p.set('month', String(ym.month)) }
      if (firma) p.set('firmaType', firma)
      return api.get<BonusResponse>(`/api/bonus/?${p}`)
    },
  })

  // Adopt the server's default period (latest month with work) on first load.
  useEffect(() => { if (!ym && data?.period) setYm(data.period) }, [ym, data])

  const shiftMonth = (delta: number) => setYm((cur) => {
    const base = cur ?? data?.period
    if (!base) return cur
    const m0 = base.month - 1 + delta
    return { year: base.year + Math.floor(m0 / 12), month: ((m0 % 12) + 12) % 12 + 1 }
  })

  const rows = useMemo(() => {
    const list = [...(data?.rows ?? [])]
    const dir = sort.dir === 'asc' ? 1 : -1
    return list.sort((a, b) => {
      const av = sortVal(a, sort.key); const bv = sortVal(b, sort.key)
      const r = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return r * dir
    })
  }, [data, sort])

  const totals = useMemo(() => rows.reduce((t, r) => ({
    days: t.days + r.days, metres: t.metres + r.metres, hours: t.hours + r.hours,
    earnings: t.earnings + r.earnings, meal: t.meal + r.meal, leaderBonus: t.leaderBonus + r.leaderBonus,
    total: t.total + r.total,
  }), { days: 0, metres: 0, hours: 0, earnings: 0, meal: 0, leaderBonus: 0, total: 0 }), [rows])

  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }))

  const period = ym ?? data?.period ?? null

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {t('subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-neutral-300 px-1 dark:border-neutral-700">
            <button onClick={() => shiftMonth(-1)} aria-label={t('previousMonth')} className="rounded px-2 py-1 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800">‹</button>
            <span className="tabular w-24 text-center text-sm font-medium">
              {period ? monthLabel(period.year, period.month) : '—'}
            </span>
            <button onClick={() => shiftMonth(1)} aria-label={t('nextMonth')} className="rounded px-2 py-1 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800">›</button>
          </div>
          <Select value={firma} onChange={setFirma} aria-label={t('firma')} className="w-fit min-w-[8rem]"
            empty={{ value: '', label: t('allFirms') }}
            options={[{ value: 'MIH', label: 'MIH' }, { value: 'SUB', label: 'SUB' }]} />
          {canEdit && data?.config && (
            <button onClick={() => setShowConfig(true)}
                    className="rounded-lg px-3 py-1.5 text-sm text-neutral-700 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-800">
              {t('settings')}
            </button>
          )}
        </div>
      </header>

      {data?.config && (
        <p className="text-xs text-neutral-400">
          {t('configNote', {
            norm: fmtEur(data.config.normPerHour, 2),
            meal: fmtEur(data.config.mealPerWorkerDay, 2),
            leader: fmtEur(data.config.crewLeaderBonus, 2),
          })}
        </p>
      )}

      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
              <th className="w-10 px-3 py-2 text-right font-medium">#</th>
              <Th col="crew" sort={sort} onSort={toggle}>{t('colCrew')}</Th>
              <th className="px-3 py-2 font-medium">{t('colFirma')}</th>
              <Th col="days" sort={sort} onSort={toggle} align>{t('colDays')}</Th>
              <Th col="metres" sort={sort} onSort={toggle} align>{t('colMetres')}</Th>
              <Th col="hours" sort={sort} onSort={toggle} align>{t('colHours')}</Th>
              <Th col="earnings" sort={sort} onSort={toggle} align>{t('colEarnings')}</Th>
              <Th col="perHour" sort={sort} onSort={toggle} align>{t('colPerHour')}</Th>
              <Th col="normPct" sort={sort} onSort={toggle} align>{t('colNorm')}</Th>
              <th className="px-3 py-2 text-right font-medium">{t('colMeal')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('colLeader')}</th>
              <Th col="total" sort={sort} onSort={toggle} align>{t('colTotal')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.crew} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40">
                <td className="tabular px-3 py-2 text-right text-xs text-neutral-400">{i + 1}</td>
                <td className="px-3 py-2 font-medium">{r.crew}</td>
                <td className="px-3 py-2 text-neutral-500">
                  {r.firma ?? '—'}
                  {r.firmaType && <span className="ml-1 text-[10px] text-neutral-400">{r.firmaType}</span>}
                </td>
                <td className="tabular px-3 py-2 text-right">{r.days}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(r.metres, '')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(r.hours, '')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(r.earnings, 'EUR')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(r.perHour, 'EUR')}</td>
                <td className="tabular px-3 py-2 text-right"><NormBadge pct={r.normPct} /></td>
                <td className="tabular px-3 py-2 text-right text-neutral-500">{r.meal ? formatValue(r.meal, 'EUR') : '—'}</td>
                <td className="tabular px-3 py-2 text-right text-neutral-500">{r.leaderBonus ? formatValue(r.leaderBonus, 'EUR') : '—'}</td>
                <td className="tabular px-3 py-2 text-right font-semibold">{formatValue(r.total, 'EUR')}</td>
              </tr>
            ))}
            {!isPending && rows.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-10 text-center text-sm text-neutral-500">{t('noProduction')}</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-neutral-200 font-semibold dark:border-neutral-700">
                <td /><td className="px-3 py-2">{t('totalCrews', { count: rows.length })}</td><td />
                <td className="tabular px-3 py-2 text-right">{totals.days}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(totals.metres, '')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(totals.hours, '')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(totals.earnings, 'EUR')}</td>
                <td /><td />
                <td className="tabular px-3 py-2 text-right">{formatValue(totals.meal, 'EUR')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(totals.leaderBonus, 'EUR')}</td>
                <td className="tabular px-3 py-2 text-right">{formatValue(totals.total, 'EUR')}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showConfig && data?.config && (
        <BonusConfigDialog config={data.config} onClose={() => setShowConfig(false)} />
      )}
    </div>
  )
}

function NormBadge({ pct }: { pct: number }) {
  const v = Math.round(pct * 100)
  const color = pct >= 1 ? 'var(--viz-good)' : pct >= 0.8 ? 'var(--viz-serious)' : 'var(--viz-critical)'
  return <span style={{ color }}>{v}%</span>
}

function Th({ col, sort, onSort, align, children }: {
  col: SortKey
  sort: { key: SortKey; dir: 'asc' | 'desc' }
  onSort: (k: SortKey) => void
  align?: boolean
  children: React.ReactNode
}) {
  const active = sort.key === col
  return (
    <th className={`px-3 py-2 font-medium ${align ? 'text-right' : ''}`}>
      <button type="button" onClick={() => onSort(col)}
              className={`inline-flex items-center gap-1 hover:text-neutral-800 dark:hover:text-neutral-200 ${
                active ? 'text-neutral-800 dark:text-neutral-200' : ''} ${align ? 'flex-row-reverse' : ''}`}>
        {children}
        <span aria-hidden className="text-[10px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  )
}
